import { useState } from 'react';
import * as React from 'react';
import { Tree } from 'antd';
import type { TreeDataNode } from 'antd';

import FormHeader from '../FormHeader';
import InfoPopover from '../../ui/InfoPopover';

interface DataSelectionProps {
    selectedDataPoint: string | null;
    setSelectedDataPoint: (dataPoint: string | null) => void;
    onVolumeOrCurrencySelected?: (isSelected: boolean) => void;
}

const infoPopoverContent = {
    adjusted: `"Adjusted" prices are stock price values that were amended to accurately reflect the given stock's value after accounting for any corporate actions, such as splits or dividends. Adjustments are made in accordance with the "CRSP Calculations" methodology set forth by the Center for Research in Security Prices (CRSP).`,
    tradingCurrency: `Returns the currency in which the selected companies share price is traded in.`,
    tradingVolume: `Returns the total number of shares traded in the selected companies shares on a given day.`,
    tradingDays: `Between a selected beginning and ending date, this formula will return each available Closing Date on which the selected company’s share price was traded. Closing Dates returned as subsequent rows in your Excel.`,
};

const DataSelection: React.FC<DataSelectionProps> = ({ selectedDataPoint, setSelectedDataPoint, onVolumeOrCurrencySelected }) => {
    const treeData: TreeDataNode[] = [
        {
            title: 'Share Prices',
            key: 'share-prices',
            children: [
                {
                    title: 'Closing Share Price',
                    key: 'Closing_Share_Price',
                    isLeaf: true,
                },
                {
                    title: 'Opening Share Price',
                    key: 'Opening_Share_Price',
                    isLeaf: true,
                },
                {
                    title: 'Daily High',
                    key: 'Daily_High',
                    isLeaf: true,
                },
                {
                    title: 'Daily Low',
                    key: 'Daily_Low',
                    isLeaf: true,
                },
                {
                    title: <span>Adjusted Closing Share Price { }<InfoPopover content={infoPopoverContent.adjusted} trigger="hover" placement='bottom' /></span>,
                    key: 'Adjusted_Closing_Share_Price',
                    isLeaf: true,
                }
            ],
        },
        {
            title: 'Volume and Trading Currency',
            key: 'volume-and-trading-currency',
            children: [
                {
                    title: <span>Trading Volume { }<InfoPopover content={infoPopoverContent.tradingVolume} trigger="hover" placement='bottom' /></span>,
                    key: 'Traded_Volume_Shares',
                    isLeaf: true,
                },
                {
                    title: <span>Trading Currency { }<InfoPopover content={infoPopoverContent.tradingCurrency} trigger="hover" placement='bottom' /></span>,
                    key: 'Trading_Currency',
                    isLeaf: true,
                },
                {
                    title: <span>Trading Days { }<InfoPopover content={infoPopoverContent.tradingDays} trigger="hover" placement='bottom' /></span>,
                    key: 'Trading_Days',
                    isLeaf: true,
                },
            ]
        },
        {
            key: 'Derived_Market_Data',
            title: 'Derived Market Data',
            children: [
                { key: 'Period_High', title: 'Period High', isLeaf: true },
                { key: 'Period_Low', title: 'Period Low', isLeaf: true },
                { key: 'Period_Median', title: 'Period Median', isLeaf: true },
                { key: 'Period_Average', title: 'Period Average', isLeaf: true },
                { key: 'Period_VWAP', title: 'Period VWAP', isLeaf: true },
                { key: 'Period_P25', title: 'Period 25th Percentile', isLeaf: true },
                { key: 'Period_P75', title: 'Period 75th Percentile', isLeaf: true },
            ],
        }
    ];

    const onSelect = (selectedKeys: React.Key[], info: any) => {
        if (selectedKeys.length === 0) {
            setSelectedDataPoint(selectedDataPoint);
            return;
        }

        if (selectedKeys.length > 0 && info.node.isLeaf) {
            const selected = selectedKeys[0].toString();
            setSelectedDataPoint(selected);
            if (onVolumeOrCurrencySelected) {
                const isVolumeOrCurrency = ['Traded_Volume_Shares', 'Trading_Currency', 'Trading_Days'].includes(selected);
                onVolumeOrCurrencySelected(isVolumeOrCurrency);
            }
        } else {
            setSelectedDataPoint(selectedDataPoint);
            if (onVolumeOrCurrencySelected) {
                const isVolumeOrCurrency = ['Traded_Volume_Shares', 'Trading_Currency', 'Trading_Days'].includes(selectedDataPoint);
                onVolumeOrCurrencySelected(isVolumeOrCurrency);
            }
        }
    };

    return (
        <div>
            <FormHeader title="Data Selection" />
            <Tree
                defaultExpandAll
                selectedKeys={[selectedDataPoint]}
                onSelect={onSelect}
                treeData={treeData}
                showLine={{ showLeafIcon: false }}
            />
        </div>
    );
};

export default DataSelection; 